import React, { useEffect, useRef } from 'react';
import './RightDrawer.css';

interface RightDrawerProps {
  open: boolean;
  title?: string;
  width?: number | string;
  onClose: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  headerExtra?: React.ReactNode; // optional content (e.g., search) placed inline with title
}

// A lightweight, reusable right-side slide-over panel (drawer) to replace modal popups
// Uses CSS transitions defined in RightDrawer.css. Focuses first focusable element on open.
const RightDrawer: React.FC<RightDrawerProps> = ({ open, title, width = 520, onClose, footer, children, headerExtra }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open && panelRef.current) {
      // Focus first focusable element inside drawer for accessibility
      const focusable = panelRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();

      // Prevent body scroll while drawer open
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prevOverflow; };
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="rd-wrapper" role="dialog" aria-modal="true" aria-label={title || 'Panel'}>
      <div className="rd-overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="rd-panel"
        ref={panelRef}
        style={{ width: typeof width === 'number' ? `${width}px` : width }}
      >
        <div className="rd-header">
          <div className="rd-header-main">
            {title && <h5 className="rd-title mb-0">{title}</h5>}
            {headerExtra && <div className="rd-header-extra">{headerExtra}</div>}
          </div>
          <button type="button" className="rd-close-btn" onClick={onClose} aria-label="Close panel">×</button>
        </div>
  <div className={`rd-body ${footer ? 'rd-body-has-footer' : ''}`}>
          {children}
        </div>
        {footer && <div className="rd-footer">{footer}</div>}
      </div>
    </div>
  );
};

export default RightDrawer;
